// NOTE: gasby-config cannot resolve `stile-shared' as a workspace package
// must use relative imports in this file!
import { BLOG_URL, PAGE_SECTIONS, SHOP_URL } from '../../stile-shared/src/constants';
import type { PageData } from '../../stile-shared/src/utils/getPageData';

export * from '../../stile-shared/src/constants';

export const SITE_LOCALE = 'en-au';

/**
 * This is to assist with keeping page data in various places of the app in sync
 * E.g. Hero & NextPage blocks, Header & Footer menus
 * One-off or unsectioned pages (e.g. home, /other/ ) don't really need to be added here,
 * their seo, hero data etc is kept within their page files.
 */
export const PAGE_DATA: PageData = {
  'what-is-stile': {
    'revision-and-mastery-tools': {
      seo: {
        title: 'New note-taking and study resources',
        description: 'Set your students up for success with scaffolded revision tools.',
      },
      hero: {
        title: 'New note-taking and study resources',
        text: 'Set your students up for success with scaffolded revision tools',
      },
      menu: {
        text: 'Revision and mastery tools',
        url: '/what-is-stile/revision-and-mastery-tools/',
      },
    },

    'whats-included': {
      seo: {
        title: "What's included?",
        description:
          "Stile is a complete, comprehensive core curriculum for high school. It's battle-tested and highly refined, having been used (and loved!) by thousands of teachers for over 10 years.",
      },
      hero: {
        title: "What's included?",
        text: "Stile is a complete, comprehensive core curriculum for high school. It's battle-tested and highly refined, having been used (and loved!) by thousands of teachers for over 10 years.",
      },
      menu: {
        text: "What's included?",
        url: '/what-is-stile/whats-included/',
      },
    },

    'inside-stiles-lessons': {
      seo: {
        title: 'Science Teaching Resources | Hands-On, Digital & Printable Lessons',
        description:
          'Discover engaging, curriculum-aligned science lessons for Years 7–10. Customisable print, hands-on, and digital resources grounded in evidence-based practice.',
      },
      hero: {
        title: 'Science Teaching Resources | Hands-On, Digital & Printable Lessons',
        text: 'Discover engaging, curriculum-aligned science lessons for Years 7–10. Customisable print, hands-on, and digital resources grounded in evidence-based practice.',
      },
      menu: { text: "Inside Stile's lessons", url: '/what-is-stile/inside-stiles-lessons/' },
    },

    'stile-x': {
      seo: {
        title: 'Stile X Workbooks: Hands-On Learning for Science Students',
        description:
          'Stile X Workbooks bring science lessons to life—on or off devices. With worksheets, glossaries, and practice tests, they help students make sense of science',
      },
      hero: {
        title: 'Stile X: Printed revision booklets',
        text: 'The ultimate student notebook and study guide',
      },
      menu: { text: 'Stile X student workbooks', url: '/what-is-stile/stile-x/' },
    },

    'professional-learning': {
      seo: {
        title: 'Professional Learning for Aus Teachers | Stile Academy',
        description:
          'Need to build your professional development? with Stile Academy. Complete PD hours, earn certifications, and evolve your teaching skills—aligned to your schedule.',
      },
      hero: {
        title: 'Integrated Professional Learning',
        text: 'Stile Professional Learning is about great science teaching',
      },
      menu: {
        text: 'Integrated Professional Learning',
        url: '/what-is-stile/professional-learning/',
      },
    },

    assessments: {
      seo: {
        title: 'Science Assessment for Australian Classrooms | Stile',
        description:
          'A complete system of diagnostic, formative, and summative science assessment designed for Australian classrooms.',
      },
      hero: {
        title: 'Assessments',
        text: 'Teach with a real-time pulse on student understanding',
      },
      menu: { text: 'Assessments', url: '/what-is-stile/assessments/' },
    },

    'responsive-support': {
      seo: {
        title: 'Responsive support: Personalised Guidance for Schools',
        description:
          'Expert science teachers providing strategic support and professional learning for educators and leaders.',
      },
      hero: {
        title: 'Responsive support',
        text: 'Expert guidance and support to help your team succeed with Stile',
      },
      menu: {
        text: 'Responsive support',
        url: '/what-is-stile/responsive-support/',
      },
    },

    'technology-for-teaching': {
      seo: {
        title: 'Technology for teaching',
        description: 'Stile is purpose-built to empower teaching and learning in the classroom',
      },
      hero: {
        title: 'Technology for teaching',
        text: 'Purpose-built to empower teaching and learning in the classroom',
      },
      menu: { text: 'Technology for teaching', url: '/what-is-stile/technology-for-teaching/' },
    },

    curriculum: {
      seo: {
        title: "Stile's science curriculum for Years 7–10",
        description:
          "Stile's curriculum comprehensively covers the Australian Curriculum, is built on evidence-based pedagogies and makes high-impact teaching strategies the path of least resistance.",
      },
      hero: {
        title: "Stile's science curriculum",
        text: "Stile's curriculum comprehensively covers the Australian Curriculum, is built on evidence-based pedagogies and makes high-impact teaching strategies the path of least resistance.",
      },
      menu: {
        text: 'Curriculum',
        url: '/what-is-stile/curriculum/',
      },
    },
  },

  'why-choose-stile': {
    'case-studies': {
      seo: {
        title: 'Case studies',
        description: 'Hear directly from science teachers and leaders on the benefits of Stile',
      },
      hero: {
        title: 'Case studies',
        text: 'Hear directly from science teachers and leaders on the benefits of Stile',
      },
      menu: {
        text: 'Case studies',
        url: '/why-choose-stile/case-studies/',
      },
    },

    benefits: {
      seo: {
        title: 'Benefits of using Stile',
        description: 'Experience the benefits of using Stile in the science classroom.',
      },
      hero: { title: 'Benefits', text: 'You’ll see the difference' },
      menu: { text: 'Benefits', url: '/why-choose-stile/benefits/' },
    },

    'high-quality-instructional-materials': {
      seo: {
        title: 'What are High-Quality Instructional Materials for Teachers? | Stile Education',
        description:
          'Teaching resources built on evidence-based research and HQIM approved. Create lessons that stick, engage students, and transform learning. Get the checklist now.',
      },
      hero: { title: 'High-quality instructional materials', text: '' },
      menu: {
        text: 'High-quality instructional materials',
        url: '/why-choose-stile/high-quality-instructional-materials/',
      },
    },

    'evidence-of-success': {
      seo: {
        title: 'Evidence of success',
        description: 'Hear from teachers who are seeing the difference.',
        uniqueToLocale: true,
      },
      hero: { title: 'Evidence of success', text: 'Teachers are seeing the difference' },
      menu: { text: 'Evidence of success', url: '/why-choose-stile/evidence-of-success/' },
    },

    'high-impact-teaching-strategies': {
      seo: {
        title: 'High impact teaching strategies',
        description:
          'Stile is purpose-built to promote the use of evidence-based teaching strategies',
        uniqueToLocale: true,
      },
      hero: {
        title: 'High impact teaching strategies',
        text: 'Purpose-built to promote the use of evidence-based teaching strategies',
      },
      menu: {
        text: 'High impact teaching strategies',
        url: '/why-choose-stile/high-impact-teaching-strategies/',
      },
    },

    stem: {
      seo: {
        title: 'A true STEM education',
        description:
          'Connect the dots with Maths, Technology and Engineering, and help students get a true STEM education.',
      },
      hero: {
        title: 'A true STEM education',
        text: 'Join the dots with Maths, Technology and Engineering',
      },
      menu: { text: 'A true STEM education', url: '/why-choose-stile/stem/' },
    },

    steam: {
      seo: {
        title: 'STEAM: Art of Science',
        description: 'Celebrating the interplay between art and STEM',
        uniqueToLocale: true,
      },
      hero: {
        title: 'STEAM: Art of Science',
        text: 'Celebrating the interplay between art and STEM',
      },
      menu: { text: 'STEAM: Art of Science', url: '/why-choose-stile/steam/' },
    },

    'literacy-and-numeracy': {
      seo: {
        title: 'Literacy and numeracy',
        description: 'Stile reinforces core literacy and numeracy skills.',
        uniqueToLocale: true,
      },
      hero: {
        title: 'Boost literacy and numeracy',
        text: 'Stile reinforces core literacy and numeracy skills',
      },
      menu: {
        text: 'Boost literacy and numeracy',
        url: '/why-choose-stile/literacy-and-numeracy/',
      },
    },

    'what-teachers-are-saying': {
      seo: {
        title: 'Recommended by teachers',
        description:
          'Thousands of teachers use Stile every week. Learn more about their experience with Stile!',
      },
      hero: {
        title: 'Recommended by teachers',
        text: 'Thousands of teachers use Stile every week',
      },
      menu: { text: 'Recommended by teachers', url: '/why-choose-stile/what-teachers-are-saying/' },
    },
    'secure-and-private': {
      seo: {
        title: 'Security and Privacy | Stile Education',
        description:
          'Stile’s commitment to privacy and security has never wavered. Trusted by 1,000+ schools, we’ve remained breach-free for over a decade. Earning top safety ratings, SOC2 certification, and recognition from Human Rights Watch.',
      },
      hero: { title: 'Secure and Private', text: '' },
      menu: {
        text: 'Secure and private',
        url: '/why-choose-stile/secure-and-private/',
      },
    },
  },

  'who-we-are': {
    apply: {
      seo: {
        title: 'Apply for a job at Stile',
        description: 'Join the Stile team and improve the state of science education globally.',
        opengraphImageSelection: 'apply',
      },
      hero: { title: '', text: '' },
      menu: { text: '', url: '/who-we-are/apply/' },
    },

    'effective-stilists': {
      seo: {
        title: 'Effective Stilists',
        description: 'Stile team values',
      },
      hero: { title: 'Effective Stilists', text: 'Our team values' },
      menu: { text: 'Effective Stilists', url: '/who-we-are/effective-stilists/' },
    },

    'our-why': {
      seo: {
        title: 'Our why',
        description: 'A world-class science education for every student',
      },
      hero: { title: 'Our why', text: 'A world-class science education for every student' },
      menu: { text: 'Our why', url: '/who-we-are/our-why/' },
    },

    'our-principles': {
      seo: {
        title: 'Our guarantees and principles',
        description: 'The core beliefs that guide everything we do',
      },
      hero: {
        title: 'Our guarantees and principles',
        text: 'The core beliefs that guide everything we do',
      },
      menu: { text: 'Our guarantees and principles', url: '/who-we-are/our-principles/' },
    },

    'our-team': {
      seo: {
        title: 'Our team',
        description: 'We share a passion for great science education',
      },
      hero: { title: 'Our team', text: 'We share a passion for great science education' },
      menu: { text: 'Our team', url: '/who-we-are/our-team/' },
    },

    'join-the-team': {
      seo: {
        title: 'Join the team',
        description: 'Stile employees share a passion for great science education.',
        opengraphImageSelection: 'apply',
      },
      hero: { title: 'Join the team', text: 'We share a passion for great science education' },
      menu: { text: 'Join the team', url: '/who-we-are/join-the-team/' },
    },

    'gender-diversity-in-stem': {
      seo: {
        title: 'Celebrating gender diversity in STEM',
        description: 'Building a more equitable future in Australia.',
      },
      hero: {
        title: 'Celebrating gender diversity in STEM',
        text: 'Building a more equitable future in Australia',
      },
      menu: { text: 'Gender diversity in STEM', url: '/who-we-are/gender-diversity-in-stem/' },
    },

    'engineering-at-stile': {
      seo: {
        title: 'Engineering at Stile',
        description: 'Building tools that improve the state of science education globally.',
      },
      hero: {
        title: 'Being a software engineer at Stile',
        text: 'Build tools that improve the state of science education globally',
      },
      menu: { text: 'Engineering at Stile', url: '/who-we-are/engineering-at-stile/' },
    },

    'hiring-process': {
      seo: {
        title: 'Hiring process',
        description:
          'Stile aspires to build a team of exceptional individuals who are a genuine delight to work with every day.',
      },
      hero: { title: 'Hiring process', text: 'Building a team of exceptional individuals' },
      menu: { text: 'Hiring process', url: '/who-we-are/hiring-process/' },
    },

    contact: {
      seo: {
        title: 'Contact us',
        description: "Stile's contact details",
      },
      hero: { title: 'Contact us', text: 'We’re here to help' },
      menu: { text: 'Contact us', url: '/who-we-are/contact/' },
    },
  },

  pedagogies: {
    '5e-instructional-model': {
      seo: {
        title: '5E Instructional Model for Science | Stile Education',
        description:
          'See the 5E instructional model in action with Stile. Every unit is designed around Engage, Explore, Explain, Elaborate, Evaluate to deepen student understanding.',
      },
      hero: {
        title: '5E Instructional Model',
        text: 'A proven framework for effective science teaching and learning',
      },
      menu: {
        text: '5E Instructional Model',
        url: '/pedagogies/5e-instructional-model/',
      },
    },
    'explicit-instruction': {
      seo: {
        title: 'Explicit Instruction in Teaching Science | Stile Education',
        description:
          'Stile lessons use explicit instruction with clear explanations, demonstrations and modelling of the learning process, giving students structured practice to show their understanding.',
      },
      hero: {
        title: 'Explicit Instruction',
        text: 'A systematic approach to effective science teaching',
      },
      menu: {
        text: 'Explicit Instruction',
        url: '/pedagogies/explicit-instruction/',
      },
    },
    'cognitive-load-theory': {
      seo: {
        title: 'Cognitive Load Theory in Teaching Science | Stile Education',
        description:
          'Stile lessons apply cognitive load theory by chunking and linking new ideas, optimising working memory so students focus and retain learning long term.',
      },
      hero: {
        title: 'Cognitive Load Theory',
        text: 'Optimising learning through cognitive load management',
      },
      menu: {
        text: 'Cognitive Load Theory',
        url: '/pedagogies/cognitive-load-theory/',
      },
    },
    'solo-taxonomy': {
      seo: {
        title: 'SOLO Taxonomy for Teaching Science | Stile Education',
        description:
          'Discover how SOLO Taxonomy’s five levels of understanding guide Stile lessons. Each question builds mastery and metacognition through structured progression.',
      },
      hero: {
        title: 'SOLO Taxonomy',
        text: 'A framework for understanding learning depth and complexity',
      },
      menu: {
        text: 'SOLO Taxonomy',
        url: '/pedagogies/solo-taxonomy/',
      },
    },
    'differentiated-instruction': {
      seo: {
        title: 'Differentiated Instruction for Science Teaching | Stile Education',
        description:
          'Stile lessons use differentiated instruction to challenge every student at an appropriate level, with customisable content and formative assessments to track progress.',
      },
      hero: {
        title: 'Differentiated Instruction',
        text: 'Tailoring learning experiences for diverse student needs',
      },
      menu: {
        text: 'Differentiated Instruction',
        url: '/pedagogies/differentiated-instruction/',
      },
    },
  },

  resources: {
    nsw: {
      seo: {
        title: 'Complete Years 7–10 Science Program for NSW Syllabus 2026',
        description:
          'Complete science program for the 2026 NSW syllabus. Customisable, curriculum-aligned and ready-to-teach with engaging, high-quality lessons and resources from Stile.',
        uniqueToLocale: true,
      },
      hero: {
        title: 'The complete NSW Science Curriculum (7–10)',
        text: 'The 2026 syllabus is here. Stile is ready.',
      },
      menu: { text: 'Stile in New South Wales', url: '/resources/nsw/' },
    },
    wa: {
      seo: {
        title: 'Complete Science Program for WA Curriculum 2026',
        description:
          'Complete science program for the revised WA Curriculum 2026. Customisable, curriculum-aligned and ready-to-teach with engaging, high-quality lessons and resources from Stile.',
        uniqueToLocale: true,
      },
      hero: {
        title: 'The complete WA Science Curriculum',
        text: 'Western Australian Curriculum aligned. Stile is ready.',
      },
      menu: { text: 'Stile in Western Australia', url: '/resources/wa/' },
    },
    'victorian-curriculum': {
      seo: {
        title: 'Stile Education for the Victorian Curriculum',
        description:
          'Teaching science has never been easier. Get up-to-date, customisable lessons, hands-on activities, and teaching resources aligned to the Victorian curriculum (Years 7–10).',
        uniqueToLocale: true,
      },
      hero: {
        title: 'New VIC Curriculum 2.0 Implementation',
        text: '',
      },
      menu: {
        text: 'Stile in Victoria',
        url: '/resources/victorian-curriculum/',
      },
    },
    'stile-blog': {
      seo: { title: '', description: '' },
      hero: { text: '', title: '' },
      menu: { text: 'Blog', url: 'https://blog.stileeducation.com/' },
    },
    'stile-shop': {
      seo: { title: '', description: '' },
      hero: { text: '', title: '' },
      menu: { text: 'Stile Shop', url: 'https://shop.stileeducation.com/' },
    },
    'parents-and-guardians': {
      seo: {
        title: 'The perfect resource for your child',
        description:
          "Helping young citizens graduate from school ready to tackle tomorrow's problems",
      },
      hero: {
        title: 'The perfect resource for your child',
        text: "Helping young citizens graduate from school ready to tackle tomorrow's problems",
      },
      menu: { text: 'For parents and guardians', url: '/resources/parents-and-guardians/' },
    },
    'planning-documents': {
      seo: {
        title: 'Curriculum planning documents',
        description: 'Get a head start on planning with our curriculum-aligned scope and sequences',
      },
      hero: {
        title: 'Curriculum planning documents',
        text: 'Get a head start on planning with our curriculum-aligned scope and sequences',
      },
      menu: {
        text: 'Curriculum planning documents',
        url: '/resources/planning-documents/',
      },
    },
    'ict-professionals': {
      seo: {
        title: 'High-quality resources built on a secure, flexible platform',
        description: 'Your security and privacy needs, covered',
      },
      hero: {
        title: 'High-quality resources built on a secure, flexible platform',
        text: 'Your security and privacy needs, covered',
      },
      menu: {
        text: 'For ICT professionals',
        url: '/resources/ict-professionals/',
      },
    },
    'lunaria-one': {
      seo: {
        title: "Stile's partnership with Lunaria One",
        description: 'Partnering to answer the question: Can plants grow on the Moon?',
      },
      hero: {
        title: 'Can plants grow on the Moon?',
        text: 'Stile is exclusively partnering with Lunaria One for an out-of-this-world mission',
      },
      menu: { text: 'Lunaria One Partnership', url: '/resources/lunaria-one/' },
    },
    'science-week-2025': {
      seo: {
        title: 'National Science Week 2025 | Free classroom activities',
        description:
          'Stile has your Science Week sorted. Access free, easy-to-run activities, escape rooms, fun competitions, engineering challenges, events and more.',
      },
      hero: {
        title: 'National Science Week 2025 | Free classroom activities',
        text: 'Stile has your Science Week sorted. Access free, easy-to-run activities, escape rooms, fun competitions, engineering challenges, events and more.',
      },
      menu: { text: 'Science Week 2025', url: '/resources/science-week/' },
    },
  },
};

/* These can vary between locales */
export const HEADER_LINK_SECTIONS = [
  {
    title: PAGE_SECTIONS['what-is-stile'],
    links: [
      PAGE_DATA['what-is-stile']['whats-included'].menu,
      PAGE_DATA['what-is-stile']['curriculum'].menu,
      PAGE_DATA['what-is-stile']['inside-stiles-lessons'].menu,
      PAGE_DATA['what-is-stile']['assessments'].menu,
      PAGE_DATA['what-is-stile']['stile-x'].menu,
      PAGE_DATA['what-is-stile']['professional-learning'].menu,
      PAGE_DATA['what-is-stile']['technology-for-teaching'].menu,
      PAGE_DATA['what-is-stile']['responsive-support'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['why-choose-stile'],
    links: [
      PAGE_DATA['why-choose-stile']['case-studies'].menu,
      PAGE_DATA['why-choose-stile']['benefits'].menu,
      PAGE_DATA['why-choose-stile']['high-quality-instructional-materials'].menu,
      PAGE_DATA['why-choose-stile']['evidence-of-success'].menu,
      PAGE_DATA['why-choose-stile']['high-impact-teaching-strategies'].menu,
      PAGE_DATA['why-choose-stile']['stem'].menu,
      PAGE_DATA['why-choose-stile']['steam'].menu,
      PAGE_DATA['why-choose-stile']['literacy-and-numeracy'].menu,
      PAGE_DATA['why-choose-stile']['what-teachers-are-saying'].menu,
      PAGE_DATA['why-choose-stile']['secure-and-private'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['pedagogies'],
    links: [
      PAGE_DATA['pedagogies']['5e-instructional-model'].menu,
      PAGE_DATA['pedagogies']['solo-taxonomy'].menu,
      PAGE_DATA['pedagogies']['explicit-instruction'].menu,
      PAGE_DATA['pedagogies']['cognitive-load-theory'].menu,
      PAGE_DATA['pedagogies']['differentiated-instruction'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['who-we-are'],
    links: [
      PAGE_DATA['who-we-are']['our-why'].menu,
      PAGE_DATA['who-we-are']['our-principles'].menu,
      PAGE_DATA['who-we-are']['our-team'].menu,
      PAGE_DATA['who-we-are']['join-the-team'].menu,
      PAGE_DATA['who-we-are']['gender-diversity-in-stem'].menu,
      PAGE_DATA['who-we-are']['contact'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['resources'],
    links: [
      PAGE_DATA['resources']['nsw'].menu,
      PAGE_DATA['resources']['wa'].menu,
      PAGE_DATA['resources']['victorian-curriculum'].menu,
      PAGE_DATA['resources']['planning-documents'].menu,
      PAGE_DATA['resources']['science-week-2025'].menu,
      PAGE_DATA['resources']['lunaria-one'].menu,
      PAGE_DATA['resources']['stile-blog'].menu,
      PAGE_DATA['resources']['stile-shop'].menu,
      PAGE_DATA['resources']['ict-professionals'].menu,
      PAGE_DATA['resources']['parents-and-guardians'].menu,
    ],
  },
];

/* These can vary between locales */
export const FOOTER_LINK_SECTIONS = [
  {
    title: PAGE_SECTIONS['what-is-stile'],
    links: [
      PAGE_DATA['what-is-stile']['curriculum'].menu,
      PAGE_DATA['what-is-stile']['inside-stiles-lessons'].menu,
      PAGE_DATA['what-is-stile']['assessments'].menu,
      PAGE_DATA['what-is-stile']['stile-x'].menu,
      PAGE_DATA['what-is-stile']['technology-for-teaching'].menu,
      PAGE_DATA['what-is-stile']['professional-learning'].menu,
      PAGE_DATA['what-is-stile']['responsive-support'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['why-choose-stile'],
    links: [
      PAGE_DATA['why-choose-stile']['case-studies'].menu,
      PAGE_DATA['why-choose-stile']['benefits'].menu,
      PAGE_DATA['why-choose-stile']['high-quality-instructional-materials'].menu,
      PAGE_DATA['why-choose-stile']['evidence-of-success'].menu,
      PAGE_DATA['why-choose-stile']['high-impact-teaching-strategies'].menu,
      PAGE_DATA['why-choose-stile']['stem'].menu,
      PAGE_DATA['why-choose-stile']['steam'].menu,
      PAGE_DATA['why-choose-stile']['literacy-and-numeracy'].menu,
      PAGE_DATA['why-choose-stile']['what-teachers-are-saying'].menu,
      PAGE_DATA['why-choose-stile']['secure-and-private'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['pedagogies'],
    links: [
      PAGE_DATA['pedagogies']['5e-instructional-model'].menu,
      PAGE_DATA['pedagogies']['solo-taxonomy'].menu,
      PAGE_DATA['pedagogies']['explicit-instruction'].menu,
      PAGE_DATA['pedagogies']['cognitive-load-theory'].menu,
      PAGE_DATA['pedagogies']['differentiated-instruction'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['who-we-are'],
    links: [
      PAGE_DATA['who-we-are']['our-why'].menu,
      PAGE_DATA['who-we-are']['our-principles'].menu,
      PAGE_DATA['who-we-are']['our-team'].menu,
      PAGE_DATA['who-we-are']['join-the-team'].menu,
      PAGE_DATA['who-we-are']['gender-diversity-in-stem'].menu,
      PAGE_DATA['who-we-are']['engineering-at-stile'].menu,
      PAGE_DATA['who-we-are']['hiring-process'].menu,
      PAGE_DATA['who-we-are']['contact'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['resources'],
    links: [
      PAGE_DATA['resources']['nsw'].menu,
      PAGE_DATA['resources']['wa'].menu,
      PAGE_DATA['resources']['victorian-curriculum'].menu,
      PAGE_DATA['resources']['planning-documents'].menu,
      PAGE_DATA['resources']['science-week-2025'].menu,
      PAGE_DATA['resources']['lunaria-one'].menu,
      PAGE_DATA['resources']['stile-blog'].menu,
      PAGE_DATA['resources']['stile-shop'].menu,
      PAGE_DATA['resources']['ict-professionals'].menu,
      PAGE_DATA['resources']['parents-and-guardians'].menu,
    ],
  },
];

export const OTHER_SITE_LINKS = [
  { text: 'Privacy policy', url: 'https://www.curriculumassociates.com/privacy/stile' },
  { text: 'Terms', url: '/other/terms/' },
  { text: 'Blog', url: BLOG_URL },
  { text: 'Resources', url: '/resources/' },
  { text: 'Shop', url: SHOP_URL },
];
